import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useTranslation } from "../i18n/LanguageProvider";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Shield,
  HardHat,
  Building2,
  Mail,
  CheckCircle,
  Loader2,
  Clock,
  Calendar,
  Activity,
  Euro,
  Car,
  Settings,
  FileText,
  Briefcase,
  Timer,
  Edit3,
  Zap,
  ExternalLink,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  Check,
  UserPlus,
  LayoutGrid,
  Coffee,
  Navigation,
  Smartphone,
  FolderKanban,
  Hotel,
  Utensils,
  MapPin,
  MessageSquare,
  Bot,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = '/api';
const AUTO_SAVE_DELAY = 500;

const roleConfig = {
  admin: { icon: Shield, label: "Admin", color: "bg-purple-100 text-purple-700" },
  projektleiter: { icon: HardHat, label: "Projektleiter", color: "bg-blue-100 text-blue-700" },
  mitarbeiter: { icon: User, label: "Mitarbeiter", color: "bg-gray-100 text-gray-700" },
  nachunternehmer: { icon: Building2, label: "Nachunternehmer", color: "bg-orange-100 text-orange-700" },
};

const getDefaultPermissions = (role) => {
  // Granulare Defaults für Bautagesberichte (siehe backend helpers.get_default_daily_report_permissions)
  const dailyReportsDefaults = {
    admin: { view: true, create: true, edit: true, pdf: true, finalize: true, duplicate: true, delete: true },
    projektleiter: { view: true, create: true, edit: true, pdf: true, finalize: true, duplicate: true, delete: false },
    nachunternehmer: { view: true, create: false, edit: false, pdf: false, finalize: false, duplicate: false, delete: false },
    mitarbeiter: { view: true, create: false, edit: false, pdf: false, finalize: false, duplicate: false, delete: false },
  };
  switch (role) {
    case "admin":
      return {
        can_manage_users: true, can_manage_projects: true, can_view_all_entries: true,
        can_approve_entries: true, can_edit_break_time: true, can_override_work_time: true,
        travel_counts_as_work: true, can_create_tasks: true, can_edit_tasks: true,
        can_view_reports: true, can_view_project_info: true, can_access_crm: true,
        daily_reports: dailyReportsDefaults.admin,
      };
    case "projektleiter":
      return {
        can_manage_users: false, can_manage_projects: true, can_view_all_entries: true,
        can_approve_entries: true, can_edit_break_time: true, can_override_work_time: false,
        travel_counts_as_work: true, can_create_tasks: true, can_edit_tasks: true,
        can_view_reports: true, can_view_project_info: true, can_access_crm: true,
        daily_reports: dailyReportsDefaults.projektleiter,
      };
    case "nachunternehmer":
      return {
        can_manage_users: false, can_manage_projects: false, can_view_all_entries: false,
        can_approve_entries: false, can_edit_break_time: false, can_override_work_time: false,
        travel_counts_as_work: false, can_create_tasks: false, can_edit_tasks: false,
        can_view_reports: true, can_view_project_info: true, can_access_crm: false,
        daily_reports: dailyReportsDefaults.nachunternehmer,
      };
    default:
      return {
        can_manage_users: false, can_manage_projects: false, can_view_all_entries: false,
        can_approve_entries: false, can_edit_break_time: false, can_override_work_time: false,
        travel_counts_as_work: false, can_create_tasks: true, can_edit_tasks: false,
        can_view_reports: true, can_view_project_info: true, can_access_crm: false,
        daily_reports: dailyReportsDefaults.mitarbeiter,
      };
  }
};

const dailyReportPermissions = [
  { key: "view", label: "Ansehen", description: "Kann Bautagesberichte des Projekts einsehen" },
  { key: "create", label: "Erstellen", description: "Kann neue Bautagesberichte anlegen" },
  { key: "edit", label: "Bearbeiten", description: "Kann bestehende Bautagesberichte speichern und ändern" },
  { key: "pdf", label: "PDF erzeugen", description: "Kann das PDF eines Bautagesberichts erzeugen / herunterladen" },
  { key: "finalize", label: "Finalisieren", description: "Kann einen Bericht von Entwurf in den Status Final überführen" },
  { key: "duplicate", label: "Duplizieren", description: "Kann einen Bericht auf ein anderes Datum kopieren" },
  { key: "delete", label: "Löschen", description: "Kann einen Bautagesbericht löschen (soft-delete)" },
];

const permissionGroups = [
  {
    title: "Verwaltung", icon: Settings,
    permissions: [
      { key: "can_manage_users", label: "Benutzer verwalten", description: "Kann Mitarbeiter anlegen, bearbeiten und löschen" },
      { key: "can_manage_projects", label: "Projekte verwalten", description: "Kann Projekte erstellen und verwalten" },
    ]
  },
  {
    title: "Zeiterfassung", icon: Timer,
    permissions: [
      { key: "can_view_all_entries", label: "Alle Einträge sehen", description: "Kann Zeiteinträge aller Mitarbeiter einsehen" },
      { key: "can_approve_entries", label: "Einträge genehmigen", description: "Kann Zeiteinträge anderer genehmigen" },
      { key: "can_override_work_time", label: "Arbeitszeit überschreiben", description: "Kann Arbeitszeiten manuell korrigieren" },
      { key: "can_edit_break_time", label: "Pausenzeit manuell aendern", description: "Kann Pausenzeiten anpassen" },
      { key: "gps_tracking_enabled", label: "GPS Zeiterfassung", description: "Standort wird beim Ein-/Ausstempeln erfasst. Ohne Aktivierung keine GPS-Daten." },
    ]
  },
  {
    title: "Projektarbeit", icon: Briefcase,
    permissions: [
      { key: "can_create_tasks", label: "Aufgaben erstellen", description: "Kann neue Aufgaben in Projekten anlegen" },
      { key: "can_edit_tasks", label: "Alle Aufgaben bearbeiten", description: "Kann auch fremde Aufgaben bearbeiten" },
      { key: "can_view_reports", label: "Berichte einsehen", description: "Kann Projektberichte lesen" },
      { key: "can_view_project_info", label: "Projektinfos sehen", description: "Kann Baustelleninfos einsehen" },
    ]
  },
  {
    title: "Spezial", icon: Zap,
    permissions: [
      { key: "travel_counts_as_work", label: "Fahrzeit = Arbeitszeit", description: "Fahrzeit wird als Arbeitszeit gewertet" },
    ]
  },
  {
    title: "CRM", icon: LayoutGrid,
    permissions: [
      { key: "can_access_crm", label: "CRM Zugriff", description: "Kann das CRM-Modul nutzen und Kontakte verwalten. Ohne CRM-Zugriff ist der Mitarbeiter im CRM-Dropdown nicht auswählbar." },
    ]
  }
];

function generatePassword(length = 12) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const special = "!@#$%&*?";
  const all = upper + lower + digits + special;
  let pw = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];
  for (let i = pw.length; i < length; i++) {
    pw.push(all[Math.floor(Math.random() * all.length)]);
  }
  for (let i = pw.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pw[i], pw[j]] = [pw[j], pw[i]];
  }
  return pw.join("");
}

function SaveStatus({ status }) {
  if (status === "idle") return null;
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
      status === "saving" ? "bg-blue-50 text-blue-700 border border-blue-200"
        : status === "saved" ? "bg-green-50 text-green-700 border border-green-200"
        : status === "error" ? "bg-red-50 text-red-700 border border-red-200" : ""
    }`} data-testid="save-status">
      {status === "saving" && <><Loader2 className="w-4 h-4 animate-spin" /><span>Speichern...</span></>}
      {status === "saved" && <><CheckCircle className="w-4 h-4" /><span>Gespeichert</span></>}
    </div>
  );
}

function CopyBtn({ text, label, children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700"
      data-testid={`copy-${label}`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Kopiert!" : (children || "Kopieren")}
    </button>
  );
}

/* Editierbares E-Mail-Feld für Admins. Local State + commit-on-Save (kein Auto-Save bei jedem Keystroke). */
function EmailEditField({ initialEmail, isAdmin, onCommit }) {
  const [val, setVal] = useState(initialEmail || "");
  const [editing, setEditing] = useState(false);
  // Sync wenn initialEmail von außen geändert wird (z.B. nach erfolgreichem Save)
  useEffect(() => { setVal(initialEmail || ""); }, [initialEmail]);

  if (!isAdmin) {
    return (
      <div className="mt-1 flex items-center gap-3">
        <Input value={val} disabled className="bg-gray-50 max-w-md" data-testid="security-email" />
        <CopyBtn text={val} label="email">Kopieren</CopyBtn>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-2 max-w-md">
      <Input
        type="email"
        value={val}
        onChange={(e) => { setVal(e.target.value); setEditing(true); }}
        onKeyDown={async (e) => {
          if (e.key === "Enter") { e.preventDefault(); const ok = await onCommit(val.trim().toLowerCase()); if (ok) setEditing(false); }
          if (e.key === "Escape") { setVal(initialEmail || ""); setEditing(false); }
        }}
        placeholder="name@firma.de"
        className="flex-1"
        data-testid="security-email"
      />
      {editing && val.trim().toLowerCase() !== (initialEmail || "").toLowerCase() ? (
        <>
          <button
            type="button"
            onClick={async () => { const ok = await onCommit(val.trim().toLowerCase()); if (ok) setEditing(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            data-testid="save-email-btn"
          >
            <Check className="w-3.5 h-3.5" />
            Speichern
          </button>
          <button
            type="button"
            onClick={() => { setVal(initialEmail || ""); setEditing(false); }}
            className="px-2 py-1.5 text-xs font-medium rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
            data-testid="cancel-email-btn"
            title="Abbrechen (ESC)"
          >
            ✕
          </button>
        </>
      ) : (
        <CopyBtn text={val} label="email">Kopieren</CopyBtn>
      )}
    </div>
  );
}

export default function MitarbeiterDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { t, setLanguage } = useTranslation();
  const isCreateMode = !userId;

  const [userData, setUserData] = useState(
    isCreateMode
      ? {
          name: "", email: "", role: "mitarbeiter", is_active: true,
          permissions: getDefaultPermissions("mitarbeiter"),
          has_fixed_schedule: true,
          work_schedule: { "0": 480, "1": 480, "2": 480, "3": 480, "4": 480, "5": 0, "6": 0 },
          soll_minutes: 480,
          default_hourly_rate: "", external_hourly_rate: "", km_allowance: "",
          extended_time_tracking: true,
        }
      : null
  );
  const [loading, setLoading] = useState(!isCreateMode);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [activeTab, setActiveTab] = useState("overview");
  const [password, setPassword] = useState(isCreateMode ? generatePassword() : "");
  const [showPassword, setShowPassword] = useState(isCreateMode);
  const [creating, setCreating] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const autoSaveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef(null);

  useEffect(() => {
    if (currentUser && !currentUser?.permissions?.can_manage_users) {
      navigate("/app");
      toast.error("Keine Berechtigung für Mitarbeiterverwaltung");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (isCreateMode) return;
    const loadUser = async () => {
      try {
        const res = await authFetch(`${API}/users`);
        const users = await res.json();
        setAllUsers(users);
        const foundUser = users.find((u) => u.id === userId);
        if (foundUser) {
          setUserData(foundUser);
        } else {
          toast.error("Mitarbeiter nicht gefunden");
          navigate("/app/mitarbeiter");
        }
      } catch {
        toast.error("Fehler beim Laden");
        navigate("/app/mitarbeiter");
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [userId, navigate, isCreateMode]);

  useEffect(() => {
    authFetch(`${API}/projects`).then(r => r.json()).then(setProjects).catch(() => {});
  }, []);


  const performAutoSave = useCallback(async (dataToSave) => {
    if (!dataToSave || isCreateMode) return;
    setSaveStatus("saving");
    try {
      const res = await authFetch(`${API}/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({
          name: dataToSave.name,
          email: dataToSave.email,
          role: dataToSave.role,
          permissions: dataToSave.permissions,
          is_active: dataToSave.is_active,
          language: dataToSave.language || "de",
          default_hourly_rate: dataToSave.default_hourly_rate ? parseFloat(dataToSave.default_hourly_rate) : null,
          external_hourly_rate: dataToSave.external_hourly_rate ? parseFloat(dataToSave.external_hourly_rate) : null,
          km_allowance: dataToSave.km_allowance ? parseFloat(dataToSave.km_allowance) : null,
          has_fixed_schedule: dataToSave.has_fixed_schedule,
          soll_minutes: dataToSave.soll_minutes ? parseInt(dataToSave.soll_minutes) : 480,
          work_schedule: dataToSave.work_schedule || null,
          auto_pause_minutes: dataToSave.auto_pause_minutes ?? 0,
          auto_pause_min_hours: dataToSave.auto_pause_min_hours ?? 6.0,
          auto_pause_condition: dataToSave.auto_pause_condition ?? "gte",
          extended_time_tracking: dataToSave.extended_time_tracking !== false,
          crm_permissions: dataToSave.crm_permissions || null,
          project_permissions: dataToSave.project_permissions || null,
          geofencing_settings: dataToSave.geofencing_settings || null,
          app_visibility: dataToSave.app_visibility || null,
          mobile_visibility: dataToSave.mobile_visibility || null,
          start_screen: dataToSave.start_screen || null,
          mobile_start_screen: dataToSave.mobile_start_screen || null,
          nav_auto_after_clockin: dataToSave.nav_auto_after_clockin !== false,
          nav_show_in_clock: dataToSave.nav_show_in_clock !== false,
          nav_popup_enabled: dataToSave.nav_popup_enabled !== false,
          guided_workday_enabled: dataToSave.guided_workday_enabled === true,
          guided_gps_enabled: dataToSave.guided_gps_enabled === true,
          guided_meal_allowance_enabled: dataToSave.guided_meal_allowance_enabled === true,
          guided_radius_meters: dataToSave.guided_radius_meters || 200,
          assigned_project_id: dataToSave.assigned_project_id || "",
          pause_minutes: dataToSave.pause_minutes ?? 30,
          event_tracking_enabled: dataToSave.event_tracking_enabled === true,
          trip_entry_enabled: dataToSave.trip_entry_enabled === true,
          time_tracking_mode: dataToSave.time_tracking_mode === "stundenzettel" ? "stundenzettel" : "stempeluhr",
          mail_account_allowed: (
            dataToSave.mail_account_allowed === true
              ? true
              : dataToSave.mail_account_allowed === false
                ? false
                : null
          ),
          nina_enabled: dataToSave.nina_enabled === true,
          nina_mobile: dataToSave.nina_mobile !== false,
          nina_projects: dataToSave.nina_projects !== false,
          nina_own_hours: dataToSave.nina_own_hours !== false,
          nina_documents: dataToSave.nina_documents !== false,
          nina_upload: dataToSave.nina_upload !== false,
          nina_tasks: dataToSave.nina_tasks !== false,
          greeting_template: dataToSave.greeting_template || "",
          greeting_name_format: dataToSave.greeting_name_format || "first_name",
          greeting_time_based: dataToSave.greeting_time_based !== false,
          home_address: dataToSave.home_address || "",
        }),
      });
      if (res.ok) {
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        setSaveStatus("error");
        toast.error("Fehler beim Speichern");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch {
      setSaveStatus("error");
      toast.error("Fehler beim Speichern");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [userId, isCreateMode]);

  const triggerAutoSave = useCallback((newData) => {
    pendingChangesRef.current = newData;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave(pendingChangesRef.current);
    }, AUTO_SAVE_DELAY);
  }, [performAutoSave]);

  const updateUserData = useCallback((updates) => {
    setUserData((prev) => {
      const newData = { ...prev, ...updates };
      if (!isCreateMode) triggerAutoSave(newData);
      return newData;
    });
  }, [triggerAutoSave, isCreateMode]);

  const updatePermission = useCallback((key, value) => {
    setUserData((prev) => {
      const newData = { ...prev, permissions: { ...prev.permissions, [key]: value } };
      if (!isCreateMode) triggerAutoSave(newData);
      return newData;
    });
  }, [triggerAutoSave, isCreateMode]);

  const updateDailyReportPermission = useCallback((key, value) => {
    setUserData((prev) => {
      const cur = prev.permissions?.daily_reports || {};
      const newData = {
        ...prev,
        permissions: {
          ...prev.permissions,
          daily_reports: { ...cur, [key]: value },
        },
      };
      if (!isCreateMode) triggerAutoSave(newData);
      return newData;
    });
  }, [triggerAutoSave, isCreateMode]);

  const handleRoleChange = useCallback((newRole) => {
    const defaultPerms = getDefaultPermissions(newRole);
    setUserData((prev) => {
      const newData = { ...prev, role: newRole, permissions: defaultPerms };
      if (!isCreateMode) triggerAutoSave(newData);
      return newData;
    });
  }, [triggerAutoSave, isCreateMode]);

  const handleCreate = async () => {
    if (!userData.name.trim()) { toast.error("Bitte Namen eingeben"); return; }
    if (!userData.email.trim()) { toast.error("Bitte E-Mail eingeben"); return; }
    if (!password.trim()) { toast.error("Bitte Passwort eingeben"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/users`, {
        method: "POST",
        body: JSON.stringify({
          name: userData.name.trim(),
          email: userData.email.trim(),
          password: password,
          role: userData.role,
          default_hourly_rate: userData.default_hourly_rate ? parseFloat(userData.default_hourly_rate) : null,
          has_fixed_schedule: userData.has_fixed_schedule,
          soll_minutes: userData.soll_minutes || 480,
          work_schedule: userData.work_schedule,
        }),
      });
      if (res.ok) {
        setCreatedCredentials({ email: userData.email.trim(), password: password, name: userData.name.trim() });
        setSuccessDialog(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Erstellen");
      }
    } catch {
      toast.error("Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!password.trim()) { toast.error("Bitte Passwort eingeben"); return; }
    setSaveStatus("saving");
    try {
      const res = await authFetch(`${API}/users/${userId}`, {
        method: "PUT",
        body: JSON.stringify({ password: password }),
      });
      if (res.ok) {
        toast.success("Passwort geändert");
        setPassword("");
        setShowPassword(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        toast.error("Fehler beim Ändern");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch {
      toast.error("Fehler beim Ändern");
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  useEffect(() => {
    return () => { if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current); };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!userData) {
    return <div className="text-center py-24"><p className="text-gray-500">Mitarbeiter nicht gefunden</p></div>;
  }

  const RoleIcon = roleConfig[userData.role]?.icon || User;
  const roleLabel = roleConfig[userData.role]?.label || userData.role;
  const roleColor = roleConfig[userData.role]?.color || "bg-gray-100 text-gray-700";

  return (
    <div data-testid="mitarbeiter-detail">
      {/* ===== STICKY HEADER ===== */}
      <div className="sticky top-0 z-30 bg-white -mx-4 px-4 pt-3 pb-0 border-b border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }} data-testid="sticky-header">
        {/* Top row: Back + Save Status */}
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/mitarbeiter")} className="gap-2 -ml-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Button>
          {!isCreateMode && <SaveStatus status={saveStatus} />}
          {isCreateMode && (
            <Button
              onClick={handleCreate}
              disabled={creating || !userData.name.trim() || !userData.email.trim() || !password.trim()}
              className="gap-2 bg-slate-800 hover:bg-slate-900"
              data-testid="create-user-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {creating ? "Erstelle..." : "Mitarbeiter anlegen"}
            </Button>
          )}
        </div>

        {/* User Info Row */}
        <div className="flex items-center gap-4 pb-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
            <RoleIcon className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[16px] font-bold text-gray-900 truncate" data-testid="user-name-display">
              {isCreateMode ? (userData.name || "Neuer Mitarbeiter") : userData.name}
            </h1>
            <div className="flex items-center gap-1.5 text-gray-500 text-[12px]">
              <Mail className="w-3 h-3" />
              <span className="truncate" data-testid="user-email-display">{isCreateMode ? (userData.email || "E-Mail eingeben...") : userData.email}</span>
            </div>
          </div>
          {!isCreateMode && (
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2.5 py-1 rounded-full text-[12px] font-medium ${userData.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`} data-testid="user-status-badge">
                {userData.is_active ? "Aktiv" : "Inaktiv"}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-[12px] font-medium ${roleColor}`} data-testid="user-role-badge">
                {roleLabel}
              </span>
            </div>
          )}
          {!isCreateMode && currentUser?.role === "admin" && userData.role !== "admin" && (
            <Button
              variant="outline" size="sm" className="gap-1.5 shrink-0"
              onClick={async () => {
                try {
                  const currentToken = localStorage.getItem("token");
                  const currentUserData = localStorage.getItem("user");
                  if (currentToken) localStorage.setItem("adminTokenBackup", currentToken);
                  if (currentUserData) localStorage.setItem("adminUserBackup", currentUserData);
                  const response = await authFetch(`${API}/admin/impersonate/${userId}`, { method: "POST" });
                  if (!response.ok) {
                    const error = await response.json();
                    toast.error(error.detail || "Impersonation fehlgeschlagen");
                    localStorage.removeItem("adminTokenBackup");
                    localStorage.removeItem("adminUserBackup");
                    return;
                  }
                  const data = await response.json();
                  localStorage.setItem("token", data.token);
                  localStorage.setItem("user", JSON.stringify({
                    id: userData.id, email: userData.email, name: userData.name,
                    role: userData.role, permissions: userData.permissions || {},
                    language: userData.language || "de",
                  }));
                  toast.success(`Arbeitsbereich von ${userData.name} geoeffnet`);
                  navigate("/app/workspace");
                  window.location.reload();
                } catch {
                  toast.error("Fehler beim Oeffnen des Arbeitsbereichs");
                  localStorage.removeItem("adminTokenBackup");
                  localStorage.removeItem("adminUserBackup");
                }
              }}
              data-testid="open-workspace-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Arbeitsbereich
            </Button>
          )}
        </div>
      </div>

      {/* ===== STICKY TABS + SCROLLABLE CONTENT ===== */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="sticky top-[97px] z-20 bg-white -mx-4 px-4 py-2 border-b border-gray-200" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} data-testid="sticky-tabs">
          <TabsList className="grid w-full bg-gray-100 rounded-lg p-1" style={{ gridTemplateColumns: "repeat(9, minmax(0, 1fr))" }}>
            <TabsTrigger value="overview" className="gap-2 text-[11px] px-1" data-testid="tab-overview">
              <FileText className="w-3.5 h-3.5" />
              Übersicht
            </TabsTrigger>
            <TabsTrigger value="workmodel" className="gap-2 text-[11px] px-1" data-testid="tab-workmodel">
              <Clock className="w-3.5 h-3.5" />
              Arbeit
            </TabsTrigger>
            <TabsTrigger value="permissions" className="gap-2 text-[11px] px-1" data-testid="tab-permissions">
              <Shield className="w-3.5 h-3.5" />
              Rechte
            </TabsTrigger>
            <TabsTrigger value="projects" className="gap-2 text-[11px] px-1" data-testid="tab-projects">
              <FolderKanban className="w-3.5 h-3.5" />
              Projekte
            </TabsTrigger>
            <TabsTrigger value="geofencing" className="gap-2 text-[11px] px-1" data-testid="tab-geofencing">
              <Navigation className="w-3.5 h-3.5" />
              GPS
            </TabsTrigger>
            <TabsTrigger value="crm" className="gap-2 text-[11px] px-1" data-testid="tab-crm">
              <Briefcase className="w-3.5 h-3.5" />
              CRM
            </TabsTrigger>
            <TabsTrigger value="appvis" className="gap-2 text-[11px] px-1" data-testid="tab-appvis">
              <Smartphone className="w-3.5 h-3.5" />
              App
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2 text-[11px] px-1" data-testid="tab-security">
              <Lock className="w-3.5 h-3.5" />
              Sicherheit
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2 text-[11px] px-1" data-testid="tab-activity">
              <Activity className="w-3.5 h-3.5" />
              Aktivität
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ===== Tab: Übersicht ===== */}
        <TabsContent value="overview" className="mt-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Stammdaten</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-700">Name</Label>
                <Input value={userData.name} onChange={(e) => updateUserData({ name: e.target.value })} placeholder="Vor- und Nachname" className="mt-1" data-testid="edit-name" />
              </div>
              {isCreateMode ? (
                <div>
                  <Label className="text-gray-700">E-Mail</Label>
                  <Input type="email" value={userData.email} onChange={(e) => updateUserData({ email: e.target.value })} placeholder="name@firma.de" className="mt-1" data-testid="edit-email" />
                </div>
              ) : (
                <div>
                  <Label className="text-gray-700">E-Mail</Label>
                  <Input value={userData.email} disabled className="mt-1 bg-gray-50" data-testid="edit-email" />
                  <p className="text-xs text-gray-500 mt-1">E-Mail kann nicht geändert werden</p>
                </div>
              )}
              <div>
                <Label className="text-gray-700">Rolle</Label>
                <Select value={userData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger className="mt-1" data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="projektleiter">Projektleiter</SelectItem>
                    <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                    <SelectItem value="nachunternehmer">Nachunternehmer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Rolle setzt Standard-Berechtigungen</p>
              </div>
              <div>
                <Label className="text-gray-700">Sprache</Label>
                <Select value={userData.language || "de"} onValueChange={(v) => {
                  updateUserData({ language: v });
                  // Wenn der eigene Benutzer bearbeitet wird, Sprache sofort anwenden
                  if (userId === currentUser?.id) setLanguage(v);
                }}>
                  <SelectTrigger className="mt-1" data-testid="edit-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="sk">Slovenčina</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Bestimmt die App-Oberfläche UND die automatische Übersetzung von Aufgaben/Kommentaren (z.B. Slovenčina für Nachunternehmer)</p>
              </div>
              {!isCreateMode && userData.permissions?.gps_tracking_enabled && (
                <div>
                  <Label className="flex items-center gap-2 text-gray-700">
                    <Navigation className="w-4 h-4" />
                    GPS-Einwilligung
                  </Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg" data-testid="gps-consent-display">
                    {userData.gps_consent_timestamp ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                        <div>
                          <p className="text-sm text-green-700 font-medium">
                            {t("gps.consent_date")}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {new Date(userData.gps_consent_timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">{t("gps.no_consent")}</p>
                    )}
                  </div>
                </div>
              )}
              {!isCreateMode && (
                <div>
                  <Label className="text-gray-700">Status</Label>
                  <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Aktiv</p>
                      <p className="text-xs text-gray-500">Kann sich einloggen und arbeiten</p>
                    </div>
                    <Switch checked={userData.is_active} onCheckedChange={(checked) => updateUserData({ is_active: checked })} data-testid="edit-active" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Abrechnungsdaten in Übersicht (edit mode) */}
          {!isCreateMode && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6 mt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Euro className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Abrechnungsdaten</h2>
                  <p className="text-sm text-gray-500">Für Projektcontrolling und Kostenberechnungen</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <Label className="flex items-center gap-2 text-gray-700"><Euro className="w-4 h-4" />Stundensatz intern (€/h)</Label>
                  <Input type="number" step="0.01" min="0" value={userData.default_hourly_rate || ""} onChange={(e) => updateUserData({ default_hourly_rate: e.target.value })} placeholder="z.B. 45.00" className="mt-1" data-testid="edit-internal-rate" />
                </div>
                <div>
                  <Label className="flex items-center gap-2 text-gray-700"><Briefcase className="w-4 h-4" />Externer Satz (€/h)</Label>
                  <Input type="number" step="0.01" min="0" value={userData.external_hourly_rate || ""} onChange={(e) => updateUserData({ external_hourly_rate: e.target.value })} placeholder="z.B. 75.00" className="mt-1" data-testid="edit-external-rate" />
                </div>
                <div>
                  <Label className="flex items-center gap-2 text-gray-700"><Car className="w-4 h-4" />KM-Pauschale (€/km)</Label>
                  <Input type="number" step="0.01" min="0" value={userData.km_allowance || ""} onChange={(e) => updateUserData({ km_allowance: e.target.value })} placeholder="z.B. 0.30" className="mt-1" data-testid="edit-km-allowance" />
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== Tab: Arbeitsmodell ===== */}
        <TabsContent value="workmodel" className="mt-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Arbeitsmodell
            </h2>

            <div>
              <Label className="text-gray-700">Arbeitszeit-Modell</Label>
              <div className="flex items-center justify-between mt-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <p className="text-sm font-medium text-gray-900">Feste Wochenarbeitszeit</p>
                  <p className="text-xs text-gray-500">
                    {userData.has_fixed_schedule !== false ? "Sollzeit wird pro Wochentag berechnet" : "Keine automatische Sollzeit"}
                  </p>
                </div>
                <Switch
                  checked={userData.has_fixed_schedule !== false}
                  onCheckedChange={(checked) => {
                    if (checked && !userData.work_schedule) {
                      updateUserData({ has_fixed_schedule: true, work_schedule: { "0": 480, "1": 480, "2": 480, "3": 480, "4": 480, "5": 0, "6": 0 } });
                    } else {
                      updateUserData({ has_fixed_schedule: checked });
                    }
                  }}
                  data-testid="edit-fixed-schedule"
                />
              </div>
            </div>

            {userData.has_fixed_schedule !== false && (
              <div className="space-y-4">
                <Label className="text-gray-700">Arbeitstage & Sollzeit pro Tag</Label>
                <div className="grid grid-cols-7 gap-2">
                  {[
                    { key: "0", label: "Mo" }, { key: "1", label: "Di" }, { key: "2", label: "Mi" },
                    { key: "3", label: "Do" }, { key: "4", label: "Fr" }, { key: "5", label: "Sa" }, { key: "6", label: "So" },
                  ].map((day) => {
                    const ws = userData.work_schedule || { "0": 480, "1": 480, "2": 480, "3": 480, "4": 480, "5": 0, "6": 0 };
                    const dm = ws[day.key] || 0;
                    const isWork = dm > 0;
                    return (
                      <div key={day.key} className={`p-3 rounded-lg border-2 transition-all ${isWork ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50"}`} data-testid={`day-config-${day.key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${isWork ? "text-blue-700" : "text-gray-500"}`}>{day.label}</span>
                          <input
                            type="checkbox" checked={isWork}
                            onChange={(e) => { const ns = { ...ws }; ns[day.key] = e.target.checked ? (userData.soll_minutes || 480) : 0; updateUserData({ work_schedule: ns }); }}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            data-testid={`day-checkbox-${day.key}`}
                          />
                        </div>
                        {isWork ? (
                          <Select value={dm.toString()} onValueChange={(v) => { const ns = { ...ws }; ns[day.key] = parseInt(v); updateUserData({ work_schedule: ns }); }}>
                            <SelectTrigger className="w-full h-8 text-xs" data-testid={`day-hours-${day.key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["240","300","360","420","450","480","510","540","600"].map(v => (
                                <SelectItem key={v} value={v}>{Math.floor(parseInt(v)/60)}:{String(parseInt(v)%60).padStart(2,"0")}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-gray-400 block text-center">Frei</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                  <span className="text-sm text-gray-600">Wochenarbeitszeit:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {(() => {
                      const s = userData.work_schedule || { "0": 480, "1": 480, "2": 480, "3": 480, "4": 480, "5": 0, "6": 0 };
                      const tot = Object.values(s).reduce((a, m) => a + (m || 0), 0);
                      const wd = Object.values(s).filter((m) => m > 0).length;
                      return `${Math.floor(tot/60)}:${String(tot%60).padStart(2,"0")} Std (${wd} Arbeitstage)`;
                    })()}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 mr-2">Vorlagen:</span>
                  {[
                    { label: "Vollzeit (Mo-Fr, 40h)", schedule: { "0": 480, "1": 480, "2": 480, "3": 480, "4": 480, "5": 0, "6": 0 } },
                    { label: "4-Tage-Woche (32h)", schedule: { "0": 480, "1": 480, "2": 480, "3": 480, "4": 0, "5": 0, "6": 0 } },
                    { label: "Teilzeit (Mo/Mi/Fr, 24h)", schedule: { "0": 480, "1": 0, "2": 480, "3": 0, "4": 480, "5": 0, "6": 0 } },
                    { label: "Halbtags (Mo-Fr, 20h)", schedule: { "0": 240, "1": 240, "2": 240, "3": 240, "4": 240, "5": 0, "6": 0 } },
                  ].map((t) => (
                    <button key={t.label} type="button" onClick={() => updateUserData({ work_schedule: t.schedule })} className="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 transition-colors">
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={`p-4 rounded-lg ${userData.has_fixed_schedule === false ? "bg-orange-50 border border-orange-200" : "bg-green-50 border border-green-200"}`}>
              <p className={`text-sm ${userData.has_fixed_schedule === false ? "text-orange-700" : "text-green-700"}`}>
                {userData.has_fixed_schedule === false ? (
                  <><strong>Flexibles Arbeitsmodell:</strong> Keine Sollzeit. Alle erfassten Stunden zählen als Plusstunden.</>
                ) : (
                  <><strong>Individuelles Arbeitsmodell:</strong> Sollzeit wird pro Wochentag berechnet. Tage ohne Häkchen = 0:00 Sollzeit.</>
                )}
              </p>
            </div>

            {/* Fahrten-Eintragung */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">Fahrten-Eintragung</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Mitarbeiter kann Fahrten manuell in der Stempeluhr nachtragen</p>
                </div>
                <Switch checked={userData.trip_entry_enabled || false} onCheckedChange={(checked) => updateUserData({ trip_entry_enabled: checked })} data-testid="trip-entry-toggle" />
              </div>
            </div>

            {/* Zeiterfassungs-Modus (Stempeluhr vs. Stundenzettel) */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">Zeiterfassungs-Modus</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Wählt den Erfassungsweg. <strong>Stundenzettel-Modus</strong> blendet die Stempeluhr aus &mdash;
                    der Mitarbeiter trägt seine Zeiten täglich als handschriftlichen Stundenzettel ein.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "stempeluhr", label: "Stempeluhr", desc: "Live ein-/ausstempeln" },
                  { v: "stundenzettel", label: "Stundenzettel", desc: "Tageszettel-Erfassung (kein Stempeln)" },
                ].map(opt => {
                  const sel = (userData.time_tracking_mode || "stempeluhr") === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => updateUserData({
                        time_tracking_mode: opt.v,
                        // Auto-Adjust Mobile-Startscreen — Stundenzettel-User landen direkt im Eingabe-Screen
                        mobile_start_screen: opt.v === "stundenzettel"
                          ? "/app/m/eingabe"
                          : (userData.mobile_start_screen === "/app/m/eingabe"
                              ? "/app/m/stempeluhr"
                              : userData.mobile_start_screen),
                      })}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${sel ? "border-amber-500 bg-amber-50" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`time-mode-${opt.v}`}
                    >
                      <div className="flex items-center gap-2">
                        {sel && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                        <span className={`font-semibold ${sel ? "text-amber-700" : "text-gray-700"}`}>{opt.label}</span>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bearbeitungs-Fenster (Edit-Lock) — Admin-Override */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center shrink-0">
                  <Timer className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">Bearbeitungs-Fenster</h2>
                  <p className="text-xs text-gray-500 mt-0.5 mb-3">
                    Wie viele Stunden nach Eingabe darf der Mitarbeiter den Stundenzettel noch ändern? Leer = globaler Standard.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="720"
                      step="1"
                      value={userData.edit_lock_hours ?? ""}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        updateUserData({ edit_lock_hours: v === "" ? null : Math.max(1, Math.min(720, parseInt(v, 10) || 12)) });
                      }}
                      placeholder="Standard"
                      className="w-24 h-10 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                      data-testid="edit-lock-hours-input"
                    />
                    <span className="text-sm text-gray-600">Stunden</span>
                    {userData.edit_lock_hours == null && (
                      <span className="text-xs text-gray-400">(globaler Standard wird verwendet)</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Email-Account erlaubt */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">Email-Postfach erlaubt</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {userData.role === "nachunternehmer"
                      ? "Nachunternehmer haben standardmässig kein eigenes Postfach. Aktivieren, um für diesen Mitarbeiter IMAP/SMTP einzurichten."
                      : "Steuert, ob Admin einen Email-Zugang für diesen Mitarbeiter in Email-Zugänge anlegen darf."}
                  </p>
                </div>
                <Switch
                  checked={userData.mail_account_allowed === true || (userData.mail_account_allowed == null && userData.role !== "nachunternehmer")}
                  onCheckedChange={(checked) => updateUserData({ mail_account_allowed: checked })}
                  data-testid="mail-account-allowed-toggle"
                />
              </div>
            </div>

            {/* Nina KI-Assistentin */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                    <Bot className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-900">Nina KI-Assistentin</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Zugriff und Funktionen für diesen Mitarbeiter</p>
                  </div>
                </div>
                <Switch checked={userData.nina_enabled || false} onCheckedChange={(checked) => updateUserData({ nina_enabled: checked })} data-testid="nina-enabled-toggle" />
              </div>

              {userData.nina_enabled && (
                <div className="mt-4 space-y-3 pt-4 border-t border-gray-100">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Freigeschaltete Funktionen</p>
                  
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { key: "nina_mobile", label: "Mobile App", desc: "Nina auf dem Handy nutzen" },
                      { key: "nina_projects", label: "Projekte", desc: "Fragen zu Projekten stellen" },
                      { key: "nina_own_hours", label: "Eigene Stunden", desc: "Eigene Arbeitszeiten und Fahrten abfragen" },
                      { key: "nina_documents", label: "Dokumente", desc: "Projekt-Dokumente suchen und fragen" },
                      { key: "nina_upload", label: "Upload-Hilfe", desc: "Nina hilft beim Dokument-Upload und Sortierung" },
                      { key: "nina_tasks", label: "Aufgaben", desc: "Offene Aufgaben und Einsatzplan abfragen" },
                    ].map(feat => (
                      <div key={feat.key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{feat.label}</p>
                          <p className="text-[10px] text-gray-500">{feat.desc}</p>
                        </div>
                        <Switch
                          checked={userData[feat.key] !== false}
                          onCheckedChange={(checked) => updateUserData({ [feat.key]: checked })}
                          data-testid={`${feat.key}-toggle`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                    <p className="text-[10px] text-amber-700 font-semibold">Immer gesperrt für Mitarbeiter:</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">Finanzen, Lohn/Gehaelter, Personalakten, Gesellschaft, Strategie, Aktionen (verschieben, erstellen)</p>
                  </div>
                </div>
              )}
            </div>

            {/* Begruessung */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Begruessung</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Individuelle Begruessung in der App</p>
                </div>
              </div>
              <div className="space-y-4">
                {/* Vorschau */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Vorschau</p>
                  <p className="text-base font-semibold text-gray-900" data-testid="greeting-preview">
                    {userData.greeting_time_based !== false ? (() => {
                      const h = new Date().getHours();
                      const prefix = h < 10 ? "Guten Morgen" : h < 17 ? "Hallo" : "Guten Abend";
                      const name = userData.greeting_name_format === "full_name" ? (userData.name || "") : (userData.name || "").split(" ")[0];
                      return `${prefix} ${name}`;
                    })() : `${userData.greeting_template || "Hallo {name}"}`.replace(/\{name\}/g, userData.greeting_name_format === "full_name" ? (userData.name || "") : (userData.name || "").split(" ")[0])}
                  </p>
                </div>
                {/* Zeitabhaengig */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Zeitabhaengige Begruessung</p>
                    <p className="text-xs text-gray-500 mt-0.5">Morgen/Tag/Abend automatisch wechseln</p>
                  </div>
                  <Switch checked={userData.greeting_time_based !== false} onCheckedChange={(checked) => updateUserData({ greeting_time_based: checked })} data-testid="greeting-time-toggle" />
                </div>
                {/* Template (nur wenn nicht zeitabhaengig) */}
                {userData.greeting_time_based === false && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 mb-2">Begruessung</p>
                    <div className="grid grid-cols-2 gap-2">
                      {["Hallo {name}", "Servus {name}", "Moin {name}", "Hey {name}", "Willkommen {name}"].map(t => (
                        <button key={t} onClick={() => updateUserData({ greeting_template: t })}
                          className={`px-3 py-2 text-xs rounded-lg border transition-all ${userData.greeting_template === t ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                          {t.replace("{name}", "")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Namensformat */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-2">Namensformat</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => updateUserData({ greeting_name_format: "first_name" })}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${(userData.greeting_name_format || "first_name") === "first_name" ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                      data-testid="greeting-name-first">Nur Vorname</button>
                    <button onClick={() => updateUserData({ greeting_name_format: "full_name" })}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${userData.greeting_name_format === "full_name" ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                      data-testid="greeting-name-full">Vorname + Nachname</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ===== Tab: Zugriffsrechte ===== */}
        <TabsContent value="permissions" className="mt-6">
          <div className="space-y-6">
            {permissionGroups.map((group) => {
              const GroupIcon = group.icon;
              return (
                <div key={group.title} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <GroupIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
                  </div>
                  <div className="space-y-4">
                    {group.permissions.map((perm) => (
                      <div key={perm.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{perm.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                        </div>
                        <Switch checked={userData.permissions?.[perm.key] || false} onCheckedChange={(checked) => updatePermission(perm.key, checked)} data-testid={`perm-${perm.key}`} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Bautagesberichte — granulare Rechte */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Bautagesberichte</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Granulare Rechte pro Aktion. Hinweis: Wer Projektleiter eines Projekts ist (Projekt-Manager), erhält automatisch alle Rechte außer Löschen – ungeachtet der Rolle.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                {dailyReportPermissions.map((perm) => (
                  <div key={perm.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{perm.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                    </div>
                    <Switch
                      checked={!!(userData.permissions?.daily_reports?.[perm.key])}
                      onCheckedChange={(checked) => updateDailyReportPermission(perm.key, checked)}
                      data-testid={`perm-daily-report-${perm.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Automatische Pausenregel */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Automatische Pausenregel</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Pause wird automatisch von der Arbeitszeit abgezogen, wenn keine manuelle Pause genommen wird</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Pausendauer</p>
                    <p className="text-xs text-gray-500 mt-0.5">Automatisch abgezogen wenn keine manuelle Pause erfolgt</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={[0, 30, 60].includes(userData.auto_pause_minutes) ? userData.auto_pause_minutes : "custom"}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "custom") return;
                        updateUserData({ auto_pause_minutes: parseInt(val) });
                      }}
                      className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:border-gray-400 min-w-[140px]"
                      data-testid="auto-pause-minutes"
                    >
                      <option value={0}>Keine (0 Min.)</option>
                      <option value={30}>30 Minuten</option>
                      <option value={60}>60 Minuten</option>
                      <option value="custom">Andere...</option>
                    </select>
                    {![0, 30, 60].includes(userData.auto_pause_minutes ?? 0) && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={120}
                          value={userData.auto_pause_minutes ?? 0}
                          onChange={(e) => updateUserData({ auto_pause_minutes: parseInt(e.target.value) || 0 })}
                          className="h-9 w-20 text-sm text-center border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:border-gray-400"
                          data-testid="auto-pause-custom"
                        />
                        <span className="text-xs text-gray-500">Min.</span>
                      </div>
                    )}
                  </div>
                </div>
                {(userData.auto_pause_minutes ?? 0) > 0 && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Bedingung</p>
                      <p className="text-xs text-gray-500 mt-0.5">Wann wird die Pause abgezogen?</p>
                    </div>
                    <select
                      value={userData.auto_pause_condition ?? "always"}
                      onChange={(e) => updateUserData({ auto_pause_condition: e.target.value })}
                      className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:border-gray-400 min-w-[200px]"
                      data-testid="auto-pause-condition"
                    >
                      <option value="always">Immer</option>
                      <option value="gte">Ab einer Mindestarbeitszeit</option>
                      <option value="lt">Unter einer Arbeitszeit</option>
                    </select>
                  </div>
                )}
                {(userData.auto_pause_minutes ?? 0) > 0 && (userData.auto_pause_condition === "gte" || userData.auto_pause_condition === "lt") && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {userData.auto_pause_condition === "gte" ? "Mindestarbeitszeit" : "Unter Arbeitszeit"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {userData.auto_pause_condition === "gte"
                          ? "Pause wird erst ab dieser Arbeitszeit abgezogen"
                          : "Pause wird nur abgezogen wenn weniger als diese Zeit gearbeitet wird"
                        }
                      </p>
                    </div>
                    <select
                      value={userData.auto_pause_min_hours ?? 6}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        updateUserData({ auto_pause_min_hours: val });
                      }}
                      className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:border-gray-400 min-w-[140px]"
                      data-testid="auto-pause-min-hours"
                    >
                      <option value={4}>4 Stunden</option>
                      <option value={5}>5 Stunden</option>
                      <option value={6}>6 Stunden</option>
                      <option value={7}>7 Stunden</option>
                      <option value={8}>8 Stunden</option>
                      <option value={9}>9 Stunden</option>
                      <option value={10}>10 Stunden</option>
                    </select>
                  </div>
                )}
                {(userData.auto_pause_minutes ?? 0) > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>Regel:</strong> {userData.auto_pause_minutes} Min. Pause wird automatisch abgezogen
                      {userData.auto_pause_condition === "gte"
                        ? `, wenn die Arbeitszeit mindestens ${userData.auto_pause_min_hours ?? 6} Stunden betraegt.`
                        : userData.auto_pause_condition === "lt"
                        ? `, wenn weniger als ${userData.auto_pause_min_hours ?? 6} Stunden gearbeitet wird.`
                        : ", unabhaengig von der Arbeitszeit."
                      }
                      {" "}Manuelle Pausen haben Vorrang.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Erweiterte Zeiterfassung */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Navigation className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Baustellen-Navigation</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Navigation zur Baustelle nach dem Einstempeln konfigurieren</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Navigation nach Einstempeln</p>
                    <p className="text-xs text-gray-500 mt-0.5">Popup "Zur Baustelle navigieren?" nach dem Einstempeln anzeigen</p>
                  </div>
                  <Switch
                    checked={userData.nav_auto_after_clockin !== false}
                    onCheckedChange={(val) => updateUserData({ nav_auto_after_clockin: val })}
                    data-testid="nav-auto-clockin-switch"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Navigation im Stempel-Screen</p>
                    <p className="text-xs text-gray-500 mt-0.5">Button "Zur Baustelle navigieren" immer im Stempel-Screen anzeigen</p>
                  </div>
                  <Switch
                    checked={userData.nav_show_in_clock !== false}
                    onCheckedChange={(val) => updateUserData({ nav_show_in_clock: val })}
                    data-testid="nav-show-in-clock-switch"
                  />
                </div>
              </div>
            </div>

            {/* Erweiterte Zeiterfassung (Original) */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Erweiterte Zeiterfassung</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Hinfahrt/Rückfahrt Felder im Stundenkonto und Stundenzettel</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Erweiterte Zeiterfassung aktiv</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {userData.extended_time_tracking !== false
                      ? "Volle Ansicht mit Hinfahrt/Rückfahrt Feldern"
                      : "Reduzierte Ansicht ohne Fahrtzeiten"}
                  </p>
                </div>
                <Switch
                  checked={userData.extended_time_tracking !== false}
                  onCheckedChange={(checked) => updateUserData({ extended_time_tracking: checked })}
                  data-testid="edit-extended-time-tracking"
                />
              </div>
              {userData.extended_time_tracking === false && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Reduzierte Ansicht:</strong> Hinfahrt/Rückfahrt Spalten werden im Stundenkonto und der Tagesübersicht ausgeblendet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ===== Tab: Projekt Rechte ===== */}
        <TabsContent value="projects" className="mt-6">
          <ProjectPermissionsSection userData={userData} updateUserData={updateUserData} allUsers={allUsers} />
        </TabsContent>

        {/* ===== Tab: Geofencing & GPS ===== */}
        <TabsContent value="geofencing" className="mt-6">
          <GeofencingSection userData={userData} updateUserData={updateUserData} />
        </TabsContent>

        {/* ===== Tab: CRM Rechte ===== */}
        <TabsContent value="crm" className="mt-6">
          <CrmPermissionsSection userData={userData} updateUserData={updateUserData} />
        </TabsContent>

        {/* ===== Tab: App Darstellung & Zugriff ===== */}
        <TabsContent value="appvis" className="mt-6">
          <AppVisibilitySection userData={userData} updateUserData={updateUserData} isCreateMode={isCreateMode} />
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Login und Sicherheit</h2>
                <p className="text-sm text-gray-500">
                  {isCreateMode ? "Zugangsdaten für den neuen Mitarbeiter festlegen" : "Passwort und Login-Einstellungen verwalten"}
                </p>
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className="text-gray-700 flex items-center gap-2"><Mail className="w-4 h-4" />E-Mail-Adresse</Label>
              {isCreateMode ? (
                <Input type="email" value={userData.email} onChange={(e) => updateUserData({ email: e.target.value })} placeholder="name@firma.de" className="mt-1 max-w-md" data-testid="security-email" />
              ) : (
                <EmailEditField
                  initialEmail={userData.email}
                  isAdmin={!!currentUser?.permissions?.can_manage_users}
                  onCommit={async (newEmail) => {
                    if (!newEmail || !newEmail.includes("@")) {
                      toast.error("Ungueltige E-Mail-Adresse");
                      return false;
                    }
                    if (newEmail === userData.email) return true;
                    if (!confirm(`E-Mail-Adresse aendern auf '${newEmail}'?\n\nDer Mitarbeiter muss sich danach mit dieser neuen E-Mail einloggen.`)) {
                      return false;
                    }
                    setSaveStatus("saving");
                    try {
                      const res = await authFetch(`${API}/users/${userId}`, {
                        method: "PUT",
                        body: JSON.stringify({ email: newEmail }),
                      });
                      if (!res.ok) {
                        const err = await res.json().catch(() => ({}));
                        toast.error(err.detail || "Fehler beim Ändern der E-Mail");
                        setSaveStatus("error");
                        return false;
                      }
                      const updated = await res.json();
                      updateUserData({ email: updated.email });
                      toast.success("E-Mail-Adresse geändert");
                      setSaveStatus("saved");
                      setTimeout(() => setSaveStatus("idle"), 2000);
                      return true;
                    } catch {
                      toast.error("Verbindungsfehler");
                      setSaveStatus("error");
                      return false;
                    }
                  }}
                />
              )}
            </div>

            {/* Password */}
            <div>
              <Label className="text-gray-700 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {isCreateMode ? "Passwort" : "Neues Passwort setzen"}
              </Label>
              <div className="mt-1 flex items-center gap-2 max-w-md">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isCreateMode ? "Passwort wird automatisch generiert" : "Neues Passwort eingeben..."}
                    className="pr-10 font-mono"
                    data-testid="security-password"
                  />
                  <button
                    type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { setPassword(generatePassword()); setShowPassword(true); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md bg-slate-100 hover:bg-slate-200 transition-colors text-slate-700 border border-slate-200 whitespace-nowrap"
                  data-testid="generate-password-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Passwort generieren
                </button>
              </div>
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <CopyBtn text={password} label="password">Passwort kopieren</CopyBtn>
                </div>
              )}
              {!isCreateMode && password && (
                <div className="mt-3">
                  <Button onClick={handlePasswordChange} variant="outline" size="sm" className="gap-2" data-testid="save-password-btn">
                    <Check className="w-4 h-4" />
                    Passwort speichern
                  </Button>
                </div>
              )}
            </div>

            {/* Login aktiv */}
            {!isCreateMode && (
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Login aktiv</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {userData.is_active
                        ? "Der Mitarbeiter kann sich einloggen und die App nutzen"
                        : "Der Login ist deaktiviert. Der Mitarbeiter kann sich nicht einloggen."}
                    </p>
                  </div>
                  <Switch checked={userData.is_active} onCheckedChange={(checked) => updateUserData({ is_active: checked })} data-testid="security-login-active" />
                </div>
              </div>
            )}

            {isCreateMode && (
              <div className="pt-4 border-t border-gray-100">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700">
                    Das Passwort wird nur direkt nach der Erstellung angezeigt und danach aus Sicherheitsgründen nicht mehr im Klartext gespeichert.
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== Tab: Aktivität ===== */}
        <TabsContent value="activity" className="mt-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Aktivitätsprotokoll</h2>
            <p className="text-sm text-gray-500">Übersicht der letzten Aktivitäten dieses Mitarbeiters.</p>
            {isCreateMode ? (
              <div className="text-center py-8 text-gray-400">
                <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aktivitätsdaten werden nach dem Erstellen verfügbar.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {[
                  { icon: User, iconBg: "bg-blue-100", iconColor: "text-blue-600", label: "Letzter Login", value: userData.last_login ? new Date(userData.last_login).toLocaleString("de-DE") : "Noch kein Login" },
                  { icon: Clock, iconBg: "bg-green-100", iconColor: "text-green-600", label: "Letzte Zeiterfassung", value: userData.last_time_entry ? new Date(userData.last_time_entry).toLocaleString("de-DE") : "Keine Zeiterfassung" },
                  { icon: Edit3, iconBg: "bg-purple-100", iconColor: "text-purple-600", label: "Letzte Änderung", value: userData.updated_at ? new Date(userData.updated_at).toLocaleString("de-DE") : "Keine Änderung" },
                  { icon: Calendar, iconBg: "bg-gray-100", iconColor: "text-gray-600", label: "Erstellt am", value: userData.created_at ? new Date(userData.created_at).toLocaleString("de-DE") : "Unbekannt" },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className={`w-10 h-10 ${item.iconBg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${item.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Success Dialog */}
      <Dialog open={successDialog} onOpenChange={(open) => { if (!open) navigate("/app/mitarbeiter"); setSuccessDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Mitarbeiter erfolgreich erstellt
            </DialogTitle>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-gray-600">
                <strong>{createdCredentials.name}</strong> wurde erstellt. Bitte teilen Sie die Zugangsdaten mit:
              </p>
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">E-Mail</p>
                  <code className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border block" data-testid="created-email">
                    {createdCredentials.email}
                  </code>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Passwort</p>
                  <code className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border block" data-testid="created-password">
                    {createdCredentials.password}
                  </code>
                </div>
              </div>

              <div className="flex gap-2">
                <CopyBtn
                  text={`Login-Daten:\nE-Mail: ${createdCredentials.email}\nPasswort: ${createdCredentials.password}`}
                  label="login-daten"
                >
                  Login Daten kopieren
                </CopyBtn>
                <CopyBtn text={createdCredentials.password} label="created-password">
                  Passwort kopieren
                </CopyBtn>
              </div>

              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                Das Passwort wird nur jetzt angezeigt und kann aus Sicherheitsgründen nicht erneut abgerufen werden.
              </p>
              <Button onClick={() => navigate("/app/mitarbeiter")} className="w-full bg-slate-800 hover:bg-slate-900" data-testid="close-success-dialog">
                Zur Mitarbeiterliste
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ======================== GEOFENCING SECTION ========================

function GeofencingSection({ userData, updateUserData }) {
  const defaults = {
    gps_required: false,
    live_gps_tracking: false,
    auto_clock_out_enabled: false,
    auto_clock_out_hours: 10,
    geofence_radius_meters: 200,
  };
  const geo = { ...defaults, ...(userData.geofencing_settings || {}) };

  const updateGeo = (key, value) => {
    const updated = { ...geo, [key]: value };
    updateUserData({ geofencing_settings: updated });
  };

  const radiusOptions = [100, 200, 500, 1000, 2000];
  const hourOptions = [6, 8, 10, 12, 14, 16];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-emerald-50">
            <Navigation className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm" data-testid="geofencing-title">GPS & Geofencing</h3>
            <p className="text-xs text-gray-500">Standort-Tracking und automatisches Ausstempeln</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* GPS Required */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700">GPS beim Stempeln erforderlich</span>
              <p className="text-xs text-gray-500">Mitarbeiter muss GPS aktivieren um ein-/auszustempeln</p>
            </div>
            <Switch
              checked={geo.gps_required}
              onCheckedChange={(v) => updateGeo("gps_required", v)}
              data-testid="geo-gps-required"
            />
          </div>

          {/* Live GPS Tracking */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700">Live-GPS Tracking</span>
              <p className="text-xs text-gray-500">Standort wird alle 5 Minuten waehrend der Arbeitszeit gesendet</p>
            </div>
            <Switch
              checked={geo.live_gps_tracking}
              onCheckedChange={(v) => updateGeo("live_gps_tracking", v)}
              data-testid="geo-live-tracking"
            />
          </div>

          {/* Geofence Radius */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Geofence-Radius</span>
                <p className="text-xs text-gray-500">Maximale Entfernung zum Projekt-Standort</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {radiusOptions.map((r) => (
                <button
                  key={r}
                  onClick={() => updateGeo("geofence_radius_meters", r)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    geo.geofence_radius_meters === r
                      ? "bg-emerald-600 text-white"
                      : "bg-white border border-gray-200 text-gray-600 hover:border-emerald-300"
                  }`}
                  data-testid={`geo-radius-${r}`}
                >
                  {r >= 1000 ? `${r / 1000}km` : `${r}m`}
                </button>
              ))}
            </div>
          </div>

          {/* Auto Clock Out */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Auto-Ausstempeln</span>
                <p className="text-xs text-gray-500">Automatisch ausstempeln nach X Stunden</p>
              </div>
              <Switch
                checked={geo.auto_clock_out_enabled}
                onCheckedChange={(v) => updateGeo("auto_clock_out_enabled", v)}
                data-testid="geo-auto-clockout"
              />
            </div>
            {geo.auto_clock_out_enabled && (
              <div className="flex gap-2 flex-wrap mt-2">
                {hourOptions.map((h) => (
                  <button
                    key={h}
                    onClick={() => updateGeo("auto_clock_out_hours", h)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      geo.auto_clock_out_hours === h
                        ? "bg-amber-600 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:border-amber-300"
                    }`}
                    data-testid={`geo-hours-${h}`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ======================== PROJECT PERMISSIONS SECTION ========================

function ProjectPermissionsSection({ userData, updateUserData, allUsers }) {
  const role = userData.role || "mitarbeiter";
  const adminDefaults = {
    project_visibility: "all",
    can_edit_all_projects: true,
    can_archive_projects: true,
  };
  const workerDefaults = {
    project_visibility: "all",
    can_edit_all_projects: false,
    can_archive_projects: false,
  };
  const defaults = (role === "admin" || role === "projektleiter") ? adminDefaults : workerDefaults;
  const pp = { ...defaults, ...(userData.project_permissions || {}) };

  const updatePP = (key, value) => {
    const updated = { ...pp, [key]: value };
    updateUserData({ project_permissions: updated });
  };

  const visibilityOptions = [
    { val: "all", label: "Alle Projekte", desc: "Sieht alle Projekte im System" },
    { val: "own", label: "Nur eigene Projekte", desc: "Sieht nur zugewiesene Projekte" },
    { val: "team", label: "Team-Projekte", desc: "Sieht Projekte des eigenen Teams" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-blue-50">
            <FolderKanban className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm" data-testid="project-permissions-title">Projekt Rechte</h3>
            <p className="text-xs text-gray-500">Sichtbarkeit und Bearbeitungsrechte bei Projekten</p>
          </div>
        </div>

        {/* Visibility */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Projekt-Sichtbarkeit</p>
          <div className="grid grid-cols-3 gap-2">
            {visibilityOptions.map((opt) => (
              <button
                key={opt.val}
                onClick={() => updatePP("project_visibility", opt.val)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  pp.project_visibility === opt.val
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-200"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`pp-visibility-${opt.val}`}
              >
                <span className={`text-sm font-medium ${pp.project_visibility === opt.val ? "text-blue-700" : "text-gray-700"}`}>
                  {opt.label}
                </span>
                <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Edit permissions */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Bearbeitungsrechte</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <span className="text-sm font-medium text-gray-700">Alle Projekte bearbeiten</span>
                <p className="text-xs text-gray-500">Kann auch Projekte bearbeiten, die nicht zugewiesen sind</p>
              </div>
              <Switch
                checked={pp.can_edit_all_projects || false}
                onCheckedChange={(v) => updatePP("can_edit_all_projects", v)}
                data-testid="pp-edit-all"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <span className="text-sm font-medium text-gray-700">Projekte archivieren</span>
                <p className="text-xs text-gray-500">Kann Projekte in den Archiv-Status versetzen</p>
              </div>
              <Switch
                checked={pp.can_archive_projects || false}
                onCheckedChange={(v) => updatePP("can_archive_projects", v)}
                data-testid="pp-archive"
              />
            </div>
          </div>
        </div>

        {/* Team Members */}
        {(role === "projektleiter" || pp.project_visibility === "team") && (
          <div className="mt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Team-Mitglieder</p>
            <p className="text-xs text-gray-500 mb-3">Diese Mitarbeiter gehoeren zum Team dieses Projektleiters</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {(allUsers || []).filter(u => u.id !== userData.id && u.is_active !== false).map(u => {
                const isInTeam = (userData.team_members || []).includes(u.id);
                return (
                  <label key={u.id} className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors ${isInTeam ? "bg-blue-50 border border-blue-200" : "bg-gray-50 border border-gray-100 hover:border-gray-200"}`} data-testid={`team-member-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={isInTeam}
                      onChange={(e) => {
                        const current = userData.team_members || [];
                        const updated = e.target.checked
                          ? [...current, u.id]
                          : current.filter(id => id !== u.id);
                        updateUserData({ team_members: updated });
                      }}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600"
                    />
                    <span className={`text-sm ${isInTeam ? "font-medium text-blue-700" : "text-gray-700"}`}>{u.name}</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{u.role}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CrmPermissionsSection({ userData, updateUserData }) {
  const role = userData.role || "mitarbeiter";
  const adminDefaults = {
    contacts_visibility: "all", can_view_ansprechpartner: true,
    can_view_notes: true, can_view_emails: true, can_view_activities: true,
    can_edit_contacts: true, can_edit_ansprechpartner: true,
    can_create_tasks: true, can_edit_tasks: true,
  };
  const workerDefaults = {
    contacts_visibility: "own", can_view_ansprechpartner: false,
    can_view_notes: false, can_view_emails: false, can_view_activities: false,
    can_edit_contacts: false, can_edit_ansprechpartner: false,
    can_create_tasks: true, can_edit_tasks: false,
  };
  const defaults = (role === "admin" || role === "projektleiter") ? adminDefaults : workerDefaults;
  const crm = { ...defaults, ...(userData.crm_permissions || {}) };

  const updateCrm = (key, value) => {
    const updated = { ...crm, [key]: value };
    updateUserData({ crm_permissions: updated });
  };

  const visibility = crm.contacts_visibility || "own";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm" data-testid="crm-permissions-title">CRM Rechte</h3>
            <p className="text-xs text-gray-500">Sichtbarkeit und Bearbeitungsrechte im CRM</p>
          </div>
        </div>

        {/* Kontakt-Sichtbarkeit */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 block">Kontakt-Sichtbarkeit</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { val: "all", label: "Alle Kunden", desc: "Sieht alle CRM-Kontakte" },
              { val: "own", label: "Nur eigene", desc: "Nur selbst erstellte Kontakte" },
              { val: "assigned", label: "Zugewiesene", desc: "Nur als Verantwortlicher" },
            ].map((opt) => (
              <button
                key={opt.val}
                onClick={() => updateCrm("contacts_visibility", opt.val)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  visibility === opt.val
                    ? "border-purple-400 bg-purple-50 ring-1 ring-purple-200"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`crm-visibility-${opt.val}`}
              >
                <p className="text-xs font-semibold text-gray-800">{opt.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Sichtbarkeits-Rechte */}
        <div className="mb-5">
          <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 block">Sichtbarkeit</label>
          <div className="space-y-2">
            <CrmToggle
              label="Ansprechpartner sehen"
              desc="Kann Ansprechpartner der Kontakte einsehen"
              checked={crm.can_view_ansprechpartner || false}
              onChange={(v) => updateCrm("can_view_ansprechpartner", v)}
              testId="crm-view-ansprechpartner"
            />
            <CrmToggle
              label="Notizen sehen"
              desc="Kann interne Notizen zu Kontakten lesen"
              checked={crm.can_view_notes || false}
              onChange={(v) => updateCrm("can_view_notes", v)}
              testId="crm-view-notes"
            />
            <CrmToggle
              label="E-Mails sehen"
              desc="Kann E-Mail-Kommunikation einsehen"
              checked={crm.can_view_emails || false}
              onChange={(v) => updateCrm("can_view_emails", v)}
              testId="crm-view-emails"
            />
            <CrmToggle
              label="Aktivitäten sehen"
              desc="Kann Verlauf und letzte Aktivitäten einsehen"
              checked={crm.can_view_activities || false}
              onChange={(v) => updateCrm("can_view_activities", v)}
              testId="crm-view-activities"
            />
          </div>
        </div>

        {/* Bearbeitungs-Rechte */}
        <div>
          <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 block">Bearbeitung</label>
          <div className="space-y-2">
            <CrmToggle
              label="Kontakte bearbeiten"
              desc="Kann Kontaktdaten aendern und neue Kontakte erstellen"
              checked={crm.can_edit_contacts || false}
              onChange={(v) => updateCrm("can_edit_contacts", v)}
              testId="crm-edit-contacts"
            />
            <CrmToggle
              label="Ansprechpartner bearbeiten"
              desc="Kann Ansprechpartner hinzufügen und ändern"
              checked={crm.can_edit_ansprechpartner || false}
              onChange={(v) => updateCrm("can_edit_ansprechpartner", v)}
              testId="crm-edit-ansprechpartner"
            />
            <CrmToggle
              label="Aufgaben erstellen"
              desc="Kann neue CRM-Aufgaben anlegen"
              checked={crm.can_create_tasks !== false}
              onChange={(v) => updateCrm("can_create_tasks", v)}
              testId="crm-create-tasks"
            />
            <CrmToggle
              label="Aufgaben bearbeiten"
              desc="Kann bestehende Aufgaben aendern und abschliessen"
              checked={crm.can_edit_tasks || false}
              onChange={(v) => updateCrm("can_edit_tasks", v)}
              testId="crm-edit-tasks"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CrmToggle({ label, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
      <div>
        <p className="text-xs font-medium text-gray-800">{label}</p>
        <p className="text-[10px] text-gray-500">{desc}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testId}
      />
    </div>
  );
}


// ======================== APP VISIBILITY SECTION ========================

const DESKTOP_MODULES = [
  { section: "OPERATIV", items: [
    { id: "projekte", label: "Projekte" },
    { id: "einsatzplanung", label: "Einsatzplanung" },
    { id: "tagesuebersicht", label: "Stundenzettel" },
    { id: "stempeluhr", label: "Stempeluhr" },
  ]},
  { section: "CONTROLLING", items: [
    { id: "admin_dashboard", label: "Admin Dashboard" },
    { id: "stundenkonten", label: "Stundenkonten" },
  ]},
  { section: "ORGANISATION", items: [
    { id: "mitarbeiter", label: "Mitarbeiter" },
    { id: "teams", label: "Teams" },
    { id: "terminplaner", label: "Terminplaner" },
    { id: "urlaub", label: "Urlaub" },
  ]},
  { section: "BUSINESS", items: [
    { id: "crm", label: "CRM" },
    { id: "gaeb", label: "GAEB / Kalkulation" },
    { id: "abrechnungen", label: "Abrechnungen" },
  ]},
  { section: "SYSTEM", items: [
    { id: "system_historie", label: "System Historie" },
  ]},
];

const MOBILE_MODULES = [
  { id: "stempeln", label: "Stempeln" },
  { id: "projekte", label: "Projekte" },
  { id: "einsatzplanung", label: "Einsatzplanung" },
  { id: "stundenkonto", label: "Stundenkonto" },
  { id: "tagesuebersicht", label: "Stundenzettel" },
  { id: "admin_dashboard", label: "Admin Dashboard" },
  { id: "system_historie", label: "System Historie" },
];

const PRESETS = {
  monteur: {
    label: "Monteur",
    desc: "Nur Projekte, Aufgaben, Stempeln",
    desktop: { projekte: true, stempeluhr: true, tagesuebersicht: true, stundenkonten: true, urlaub: true },
    mobile: { stempeln: true, projekte: true, tagesuebersicht: true, stundenkonto: true },
    start: "/app/stempeluhr",
    mobileStart: "/app/m/stempeluhr",
  },
  projektleiter: {
    label: "Projektleiter",
    desc: "Projekte, Berichte, Einsatzplanung",
    desktop: { projekte: true, einsatzplanung: true, tagesuebersicht: true, stempeluhr: true, stundenkonten: true, terminplaner: true, urlaub: true },
    mobile: { stempeln: true, projekte: true, einsatzplanung: true, stundenkonto: true, tagesuebersicht: true },
    start: "/app",
    mobileStart: "/app/m/projekte",
  },
  admin: {
    label: "Admin",
    desc: "Alle Bereiche sichtbar",
    desktop: null,
    mobile: null,
    start: "/app/dashboard-admin",
    mobileStart: "/app/m/dashboard-admin",
  },
};

const START_SCREENS = [
  { value: "", label: "Standard (Rolle)" },
  { value: "/app/dashboard-admin", label: "Admin Dashboard" },
  { value: "/app", label: "Projekte" },
  { value: "/app/stempeluhr", label: "Stempeluhr" },
  { value: "/app/tagesuebersicht", label: "Stundenzettel" },
  { value: "/app/crm", label: "CRM" },
];

const MOBILE_START_SCREENS = [
  { value: "", label: "Standard (Rolle)" },
  { value: "/app/m/eingabe", label: "Stundenzettel (Eingabe)" },
  { value: "/app/m/stundenkonto", label: "Stundenkonto" },
  { value: "/app/m/stempeluhr", label: "Stempeluhr" },
  { value: "/app/m/projekte", label: "Projekte" },
  { value: "/app/m/dashboard-admin", label: "Admin Dashboard" },
];

function AppVisibilitySection({ userData, updateUserData, isCreateMode }) {
  const vis = userData.app_visibility || {};
  const mVis = userData.mobile_visibility || {};

  const setDesktop = (id, val) => {
    updateUserData({ app_visibility: { ...vis, [id]: val } });
  };
  const setMobile = (id, val) => {
    updateUserData({ mobile_visibility: { ...mVis, [id]: val } });
  };

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    const deskVis = preset.desktop;
    const mobVis = preset.mobile;
    const updates = {
      start_screen: preset.start || "",
      mobile_start_screen: preset.mobileStart || "",
    };
    if (deskVis === null) {
      updates.app_visibility = {};
    } else {
      const full = {};
      DESKTOP_MODULES.forEach(s => s.items.forEach(i => { full[i.id] = !!deskVis[i.id]; }));
      updates.app_visibility = full;
    }
    if (mobVis === null) {
      updates.mobile_visibility = {};
    } else {
      const full = {};
      MOBILE_MODULES.forEach(i => { full[i.id] = !!mobVis[i.id]; });
      updates.mobile_visibility = full;
    }
    updateUserData(updates);
  };

  return (
    <div className="space-y-6" data-testid="appvis-section">
      {/* Preset Profiles */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Preset Profile</h2>
            <p className="text-sm text-gray-500">Mit einem Klick die passende Konfiguration setzen</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className="p-4 border border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left group"
              data-testid={`preset-${key}`}
            >
              <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700">{p.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Visibility */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Desktop Sichtbarkeit</h2>
            <p className="text-sm text-gray-500">Module in der Sidebar ein-/ausblenden</p>
          </div>
        </div>
        <div className="space-y-4">
          {DESKTOP_MODULES.map(group => (
            <div key={group.section}>
              <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase mb-2">{group.section}</p>
              <div className="grid grid-cols-2 gap-2">
                {group.items.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <span className="text-sm text-gray-700">{item.label}</span>
                    <Switch
                      checked={vis[item.id] !== false}
                      onCheckedChange={(val) => setDesktop(item.id, val)}
                      data-testid={`desk-vis-${item.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Startbildschirm */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <ExternalLink className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Desktop Startbildschirm</h2>
            <p className="text-sm text-gray-500">Welche Seite nach dem Login angezeigt wird</p>
          </div>
        </div>
        <Select value={userData.start_screen || ""} onValueChange={(val) => updateUserData({ start_screen: val })}>
          <SelectTrigger className="w-full" data-testid="start-screen-select">
            <SelectValue placeholder="Standard (nach Rolle)" />
          </SelectTrigger>
          <SelectContent>
            {START_SCREENS.map(s => (
              <SelectItem key={s.value || "default"} value={s.value || "default"}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile Visibility */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Mobile Module anzeigen</h2>
            <p className="text-sm text-gray-500">Module in der mobilen Navigation ein-/ausblenden</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {MOBILE_MODULES.map(item => (
            <div key={item.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100">
              <span className="text-sm text-gray-700">{item.label}</span>
              <Switch
                checked={mVis[item.id] !== false}
                onCheckedChange={(val) => setMobile(item.id, val)}
                data-testid={`mob-vis-${item.id}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Startbildschirm */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Mobile Startbildschirm</h2>
            <p className="text-sm text-gray-500">Welche Seite nach Login in der App angezeigt wird</p>
          </div>
        </div>
        <Select value={userData.mobile_start_screen || ""} onValueChange={(val) => updateUserData({ mobile_start_screen: val })}>
          <SelectTrigger className="w-full" data-testid="mobile-start-screen-select">
            <SelectValue placeholder="Standard (nach Rolle)" />
          </SelectTrigger>
          <SelectContent>
            {MOBILE_START_SCREENS.map(s => (
              <SelectItem key={s.value || "default"} value={s.value || "default"}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
